import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Lock, Crown, Sparkles, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const TIER_FEATURES = {
  "watch-all": [
    "Watch ALL performance videos",
    "Access video trailers in HD",
    "New videos before public release"
  ],
  "silver": [
    "Everything in Watch All",
    "Full access to ALL courses",
    "Purchase 4K content",
    "Access to exclusive pages"
  ],
  "gold": [
    "Everything in Silver",
    "One FREE video download monthly",
    "VIP first look at content",
    "Quarterly video call with Mestre",
    "Guest list for live events"
  ]
};

const TIER_PRICES = {
  "watch-all": 2.99,
  "silver": 9.99,
  "gold": 49.99
};

const TIER_NAMES = {
  "watch-all": "Watch All Videos",
  "silver": "Silver",
  "gold": "Gold"
};

const TIER_COLORS = {
  "watch-all": "from-blue-500 to-cyan-500",
  "silver": "from-gray-400 to-gray-300",
  "gold": "from-amber-500 to-yellow-400"
};

export const UpgradeModal = ({ isOpen, onClose, requiredTier, contentTitle }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const tierName = TIER_NAMES[requiredTier] || requiredTier;
  const tierPrice = TIER_PRICES[requiredTier] || 0;
  const tierFeatures = TIER_FEATURES[requiredTier] || [];
  const tierColor = TIER_COLORS[requiredTier] || "from-primary to-primary";

  const handleUpgrade = () => {
    onClose();
    navigate('/membership');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-card border border-border rounded-3xl max-w-md w-full overflow-hidden shadow-2xl">
              {/* Header with gradient */}
              <div className={`bg-gradient-to-r ${tierColor} p-6 text-center relative`}>
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                
                <h2 className="text-2xl font-bold text-white mb-2">
                  Premium Content
                </h2>
                <p className="text-white/80 text-sm">
                  {contentTitle ? `"${contentTitle}" requires` : 'This content requires'} a {tierName} membership
                </p>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Plan Badge */}
                <div className="flex items-center justify-center gap-2 mb-6">
                  <Crown className={`w-5 h-5 ${requiredTier === 'gold' ? 'text-amber-500' : requiredTier === 'silver' ? 'text-gray-400' : 'text-blue-500'}`} />
                  <span className="text-xl font-bold">{tierName}</span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-xl font-bold text-primary">${tierPrice}/mo</span>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-6">
                  {tierFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-green-500" />
                      </div>
                      <span className="text-sm text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <button
                  onClick={handleUpgrade}
                  className={`w-full py-4 rounded-xl font-bold text-lg bg-gradient-to-r ${tierColor} text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}
                >
                  <Sparkles className="w-5 h-5" />
                  Upgrade to {tierName}
                </button>

                <p className="text-center text-xs text-muted-foreground mt-4">
                  Cancel anytime • Instant access
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default UpgradeModal;
